import React, {
  KeyboardEvent,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import styles from './tooltip.module.css';
import clsx from 'clsx';
import { Icon } from '@pvh-components/icon';
import { useToolTipPosition } from './hooks/useToolTipPosition';
import { elementPositionToViewPort } from './elementPositionToViewPort';

export interface TooltipProps {
  className?: string | string[];
  toolTipTrigger: React.ReactNode;
  toolTipContent: React.ReactNode;
  dataTestIdPrefix?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  toolTipTrigger,
  toolTipContent,
  className,
  dataTestIdPrefix,
}) => {
  const classNames = clsx(
    {
      [styles.Tooltip]: true,
    },
    [className]
  );
  const [pos, setPos] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });
  const [isToolTipOpen, setIsToolTipOpen] = useState(false);
  const [isToolTipActive, setIsToolTipActive] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const toolTipWrapperRef = useRef<HTMLDivElement | null>(null);
  const toolTipContentRef = useRef<HTMLDivElement | null>(null);
  const toolTipPosition = useToolTipPosition(pos);

  const showToolTip = useCallback(() => {
    setIsToolTipOpen(true);
    setIsToolTipActive(true);
  }, []);

  const hideToolTip = () => {
    setIsToolTipOpen(false);
    setIsToolTipActive(false);
  };

  const toolTipActiveHandler = () => {
    showToolTip();
  };

  const toolTipDeActiveHandler = useCallback(() => {
    hideToolTip();
  }, []);

  const keyUpHandler = (e: KeyboardEvent<HTMLButtonElement | HTMLElement>) => {
    if (e.key === 'Escape') {
      hideToolTip();
    }
  };

  useEffect(() => {
    const triggerPos = elementPositionToViewPort<HTMLButtonElement>(
      triggerRef.current
    );
    if (triggerPos) {
      setPos(triggerPos);
    }
  }, [isToolTipOpen, isToolTipActive]);

  return (
    <div
      data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-tooltip` : 'tooltip'}
      className={classNames}
    >
      <button
        className={styles.ToolTipTriggerButton}
        type="button"
        tabIndex={0}
        ref={triggerRef}
        data-testid={
          dataTestIdPrefix
            ? `${dataTestIdPrefix}-tooltip-trigger`
            : 'tooltip-trigger'
        }
        onMouseOver={showToolTip}
        onTouchStart={showToolTip}
        onMouseOut={hideToolTip}
        onFocus={showToolTip}
        onKeyUp={keyUpHandler}
      >
        {toolTipTrigger}
      </button>
      {(isToolTipOpen || isToolTipActive) && (
        <div
          role="tooltip"
          ref={toolTipWrapperRef}
          data-testid={
            dataTestIdPrefix
              ? `${dataTestIdPrefix}-tooltip-content`
              : 'tooltip-content'
          }
          className={`${styles.ToolTipWrapper} ${styles[toolTipPosition]}`}
          onMouseEnter={toolTipActiveHandler}
          onMouseLeave={toolTipDeActiveHandler}
          onFocus={toolTipActiveHandler}
          onKeyUp={keyUpHandler}
        >
          <div
            tabIndex={1}
            ref={toolTipContentRef}
            className={`${styles.ToolTipContent} ${styles[toolTipPosition]}`}
          >
            {toolTipContent}
          </div>
          <button
            type="button"
            title="ToolTip Close Button"
            className={styles.ToolTipCloseButton}
            data-testid={
              dataTestIdPrefix
                ? `${dataTestIdPrefix}-tooltip-close-button`
                : 'tooltip-close-button'
            }
            onClick={hideToolTip}
          >
            <Icon width={12} height={12} className={styles.ToolTipCloseIcon} />
          </button>
        </div>
      )}
    </div>
  );
};
